import type React from 'react';
import { useId } from 'react';

import {
  Box,
  FieldSet,
  FormField,
  Input,
  Label,
  LabeledControl,
  Legend,
  Radio,
  Text,
  Textarea,
} from '@zillow/constellation-10';

import type { Section } from '../../../models/index';
import { PESTICIDES } from '../../../constants';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function Pesticides({ section, setSectionValues }: Props) {
  const radioGroupName = useId();

  function onPestControlledChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PESTICIDES.FIELD_NAME.PEST_CONTROLLED]: value,
    });
  }

  function onNameAndBrandChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PESTICIDES.FIELD_NAME.NAME_AND_BRAND]: value,
    });
  }

  function onPesticidesLocationChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PESTICIDES.FIELD_NAME.PESTICIDES_LOCATION]: value,
    });
  }

  function onScheduleChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PESTICIDES.FIELD_NAME.SCHEDULE]: value,
    });
  }

  function onRadioChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;
    setSectionValues({
      [PESTICIDES.FIELD_NAME.NO_PESTICIDES]: value === PESTICIDES.FIELD_NAME.NO_PESTICIDES,
      [PESTICIDES.FIELD_NAME.REGISTERED_PEST_CONTROL_COMPANY]:
        value === PESTICIDES.FIELD_NAME.REGISTERED_PEST_CONTROL_COMPANY,
      [PESTICIDES.FIELD_NAME.LANDLORD_LOCATION]: value === PESTICIDES.FIELD_NAME.LANDLORD_LOCATION,
    });
  }

  const currentValue = section.getFieldValue(PESTICIDES.FIELD_NAME.NO_PESTICIDES)
    ? PESTICIDES.FIELD_NAME.NO_PESTICIDES
    : section.getFieldValue(PESTICIDES.FIELD_NAME.REGISTERED_PEST_CONTROL_COMPANY)
      ? PESTICIDES.FIELD_NAME.REGISTERED_PEST_CONTROL_COMPANY
      : section.getFieldValue(PESTICIDES.FIELD_NAME.LANDLORD_LOCATION)
        ? PESTICIDES.FIELD_NAME.LANDLORD_LOCATION
        : '';

  const landlordLocationSelected = section.getFieldValue(PESTICIDES.FIELD_NAME.LANDLORD_LOCATION);

  return (
    <>
      <FieldSet required legend={<Legend>Will there be routine pesticide use at the property?</Legend>}>
        <LabeledControl
          label={<Label>No pesticide use</Label>}
          control={
            <Radio
              name={radioGroupName}
              value={PESTICIDES.FIELD_NAME.NO_PESTICIDES}
              checked={currentValue === PESTICIDES.FIELD_NAME.NO_PESTICIDES}
              onChange={onRadioChange}
              disabled={section.isFieldDisabled(PESTICIDES.FIELD_NAME.NO_PESTICIDES)}
            />
          }
        />
        <LabeledControl
          label={
            <Label>
              Yes, by a registered pest control company
              <br />
              <Text css={{ color: 'text.subtle' }}>
                You must provide the pest control company’s notice regarding pests controlled, the pesticides used, and
                a warning regarding toxic chemicals.
              </Text>
            </Label>
          }
          control={
            <Radio
              name={radioGroupName}
              value={PESTICIDES.FIELD_NAME.REGISTERED_PEST_CONTROL_COMPANY}
              checked={currentValue === PESTICIDES.FIELD_NAME.REGISTERED_PEST_CONTROL_COMPANY}
              onChange={onRadioChange}
              disabled={section.isFieldDisabled(PESTICIDES.FIELD_NAME.REGISTERED_PEST_CONTROL_COMPANY)}
            />
          }
        />
        <LabeledControl
          label={<Label>Yes, by landlord or other authorized agent</Label>}
          control={
            <Radio
              name={radioGroupName}
              value={PESTICIDES.FIELD_NAME.LANDLORD_LOCATION}
              checked={currentValue === PESTICIDES.FIELD_NAME.LANDLORD_LOCATION}
              onChange={onRadioChange}
              disabled={section.isFieldDisabled(PESTICIDES.FIELD_NAME.LANDLORD_LOCATION)}
            />
          }
        />
      </FieldSet>
      {landlordLocationSelected && (
        <Box css={{ marginTop: 'layout.tight' }}>
          <FormField
            required
            label={<Label>Name of pest or pests to be controlled</Label>}
            control={
              <Input
                aria-labelledby="controlled-pest-name"
                value={section.getFieldValue(PESTICIDES.FIELD_NAME.PEST_CONTROLLED)}
                onChange={onPestControlledChange}
                css={{ marginBottom: 'layout.tight' }}
              />
            }
          />
          <FormField
            required
            label={<Label>Name and brand of pesticide used</Label>}
            control={
              <Input
                aria-labelledby="name-and-brand"
                value={section.getFieldValue(PESTICIDES.FIELD_NAME.NAME_AND_BRAND)}
                onChange={onNameAndBrandChange}
                css={{ marginBottom: 'layout.tight' }}
              />
            }
          />
          <FormField
            required
            label={<Label>Where will you apply pesticides?</Label>}
            control={
              <Input
                aria-labelledby="pesticide-location"
                value={section.getFieldValue(PESTICIDES.FIELD_NAME.PESTICIDES_LOCATION)}
                onChange={onPesticidesLocationChange}
                css={{ marginBottom: 'layout.tight' }}
              />
            }
          />
          <FormField
            required
            label={<Label>Schedule for routine pesticide application</Label>}
            control={
              <Textarea
                aria-labelledby="pesticide-schedule"
                value={section.getFieldValue(PESTICIDES.FIELD_NAME.SCHEDULE)}
                onChange={onScheduleChange}
              />
            }
          />
        </Box>
      )}
    </>
  );
}

Pesticides.description = 'Asks Irene whether or not there is pesticide use on the property';

export default Pesticides;
